<template>
  <Card class="max-w-448 mx-auto form-stepper-card">
    <h1 class="text-h2 centered my-24">How often will <span class="linebreak">you need care?</span></h1>
    <form>
      <Card
        :clickable="true"
        class="w-full mb-12 !p-0"
        :selected="selectedSchedule === ScheduleTypes.REGULAR"
        @click="selectSchedule(ScheduleTypes.REGULAR)"
      >
        <div class="flex flex-row gap-8 items-center">
          <RegularScheduleIcon />
          <div class="flex flex-col gap-6">
            <h3 class="text-strong-body">Regular sitter</h3>
            <p class="text-small">Weekly, before/after school, driving</p>
          </div>
        </div>
      </Card>

      <Card
        :clickable="true"
        class="w-full mb-12 !p-0"
        :selected="selectedSchedule === ScheduleTypes.ONE_TIME"
        @click="selectSchedule(ScheduleTypes.ONE_TIME)"
      >
        <div class="flex flex-row gap-8 items-center">
          <OneTimeScheduleIcon />
          <div class="flex flex-col gap-6">
            <h3 class="text-strong-body">One-time sitter</h3>
            <p class="text-small">Date night, upcoming event, backup</p>
          </div>
        </div>
      </Card>

      <Card
        :clickable="true"
        class="w-full mb-12 !p-0"
        :selected="selectedSchedule === ScheduleTypes.OCCASIONAL"
        @click="selectSchedule(ScheduleTypes.OCCASIONAL)"
      >
        <div class="flex flex-row gap-8 items-center">
          <OccasionalScheduleIcon />
          <div class="flex flex-col gap-6">
            <h3 class="text-strong-body">Occasional sitter</h3>
            <p class="text-small">As-needed for when you need care</p>
          </div>
        </div>
      </Card>

      <Card
        :clickable="true"
        class="w-full mb-12 !p-0"
        :selected="selectedSchedule === ScheduleTypes.NANNY"
        @click="selectSchedule(ScheduleTypes.NANNY)"
      >
        <div class="flex flex-row gap-8 items-center">
          <NannyScheduleIcon />
          <div class="flex flex-col gap-6">
            <h3 class="text-strong-body">Nanny</h3>
            <p class="text-small">Ongoing, usually 2-5 full weekdays</p>
          </div>
        </div>
      </Card>

      <HelperText v-if="errors.selectSchedule" :text="errors.selectSchedule" :status="HelperStatus.Error" />

      <div class="flex flex-row-reverse mt-24">
        <RouteStepperNextButton :draftData="draftData" :validate="validate" @errors="errors = $event" />
      </div>
    </form>
  </Card>
</template>

<script lang="ts">
// Types
import { PropType } from 'vue';
import { JobPostDraft, ScheduleTypes } from './types';
import { HelperStatus } from '@/helpers/status';

// Components
import Card from '@/components/card/Card.vue';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';
import HelperText from '@/components/helper_text/HelperText.vue';

// Icons
import RegularScheduleIcon from '@/icons/RegularScheduleIcon.vue';
import OneTimeScheduleIcon from '@/icons/OneTimeScheduleIcon.vue';
import OccasionalScheduleIcon from '@/icons/OccasionalScheduleIcon.vue';
import NannyScheduleIcon from '@/icons/NannyScheduleIcon.vue';

interface Errors {
  [field: string]: string;
}

interface JobPostTypeOfCareData {
  selectedSchedule: ScheduleTypes;
  errors: Errors;
}

export default {
  name: 'JobPostSelectSchedule',
  components: {
    Card,
    RegularScheduleIcon,
    OneTimeScheduleIcon,
    OccasionalScheduleIcon,
    NannyScheduleIcon,
    RouteStepperNextButton,
    HelperText,
  },
  props: {
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  data(): JobPostTypeOfCareData {
    return {
      selectedSchedule: this.draft.scheduleType || ScheduleTypes.REGULAR,
      errors: {
        selectSchedule: '',
      },
    };
  },
  computed: {
    ScheduleTypes(): typeof ScheduleTypes {
      // this is needed to relay the enum import to the template
      return ScheduleTypes;
    },
    HelperStatus(): typeof HelperStatus {
      // this is needed to relay the enum import to the template
      return HelperStatus;
    },
    draftData(): JobPostDraft {
      return {
        scheduleType: this.selectedSchedule,
      };
    },
  },
  methods: {
    validate() {
      const errors: Record<string, string> = {};

      if (!this.selectedSchedule) {
        errors.selectSchedule = 'Please select a schedule type.';
      }

      return errors;
    },
    selectSchedule(scheduleType: ScheduleTypes) {
      this.selectedSchedule = scheduleType;
    },
  },
};
</script>

<style scoped lang="scss">
.form-stepper-card {
  @media (max-width: 480px) {
    padding: 0;
    background-color: #fbfbfb !important;
  }
}
</style>
